/**
 * Regex pattern to match bare ampersands that aren't already part of valid entity references.
 * Valid entity references that are NOT escaped:
 * - Named entities: &amp; &lt; &gt; &quot; &apos;
 * - Numeric entities: &#123; &#x1F600;
 *
 * Edge cases:
 * - `&Jerry` (no semicolon) IS escaped to `&amp;Jerry` - this is correct
 * - `&Jerry;` (with semicolon) is NOT escaped because it looks like an entity reference,
 *   but it will still cause an XML parse error since `Jerry` isn't a valid XML entity.
 *   XML only has 5 built-in named entities: amp, lt, gt, quot, apos.
 *   If this becomes a problem, tighten the regex to: /&(?!(?:amp|lt|gt|quot|apos|#[0-9]+|#x[0-9a-fA-F]+);)/g
 */
const BARE_AMPERSAND_PATTERN = /&(?!(?:[a-zA-Z][a-zA-Z0-9]*|#[0-9]+|#x[0-9a-fA-F]+);)/g;

/**
 * Escapes bare ampersands in XML attribute values that aren't already part of valid entity references.
 * This handles cases where URLs contain query parameters (e.g., `?foo=1&bar=2`)
 * which need to be escaped as `&amp;` for valid XML.
 */
export const escapeAmpersandsInAttributes = (xml: string): string => {
  // Match attribute values (content between quotes in attributes)
  // This regex finds: attribute="value" or attribute='value'
  // XML attribute names: start with letter, underscore, or colon; followed by letters, digits, underscores, hyphens, colons, or periods
  return xml.replace(
    /([A-Za-z_:][A-Za-z0-9._:-]*)=(["'])(.*?)\2/g,
    (_match, attrName, quote, attrValue) => {
      const escapedValue = attrValue.replace(BARE_AMPERSAND_PATTERN, '&amp;');
      return `${attrName}=${quote}${escapedValue}${quote}`;
    },
  );
};

/**
 * Escapes bare ampersands in XML text content (between tags) that aren't already part of valid entity references.
 * This handles cases like `<paragraph>Tom & Jerry</paragraph>` which need the `&` escaped as `&amp;`.
 */
export const escapeAmpersandsInTextContent = (xml: string): string => {
  // Match text content between > and <
  return xml.replace(/>([^<]+)</g, (_match, textContent) => {
    const escapedContent = textContent.replace(BARE_AMPERSAND_PATTERN, '&amp;');
    return `>${escapedContent}<`;
  });
};

/**
 * Escapes bare ampersands in both attribute values and text content.
 * Use this as the primary function for sanitizing XML before parsing.
 */
export const escapeAmpersands = (xml: string): string => {
  return escapeAmpersandsInTextContent(escapeAmpersandsInAttributes(xml));
};
